import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Global audio player state. One <audio> element lives in GlobalAudioPlayer
 * and is driven by this slice. Track rows across pages (My Songs, Library,
 * generator "Done" card) call `playTrack(trackId, queue)` to load a track,
 * then don't render their own <audio> — everything flows through the
 * single sticky player at the bottom.
 *
 * `queue` is a flat array of tracks the player will walk through with
 * next/prev. Rebuilt on each `playTrack` call from whatever list the user
 * was looking at (e.g. the current My Songs filter, or a playlist).
 */
export interface PlayerTrack {
  id: string;               // stable id — usually a Mongo _id or a client uuid
  title: string;
  artist?: string;          // "MixGenie" for generated, filename for local
  coverUrl?: string;        // optional artwork
  audioUrl: string;         // signed URL for generated songs; blob: URL for local files
  duration?: number;        // seconds; filled in by <audio> loadedmetadata if not provided
  meta?: Record<string, any>; // free-form (e.g. job doc snapshot) — no logic depends on this
}

interface PlayerState {
  queue: PlayerTrack[];
  index: number;   // -1 when nothing playing
  isPlaying: boolean;
  progress: number; // seconds within current track
  duration: number; // seconds
  volume: number;   // 0..1
  repeatMode: 'off' | 'one' | 'all';
  shuffle: boolean;
  // When true, the full-screen "now playing" view is open on top of
  // the sticky bar. Toggled by tapping the sticky bar.
  expanded: boolean;
  // Session-only set of track ids that failed to load (expired URL,
  // deleted file, corrupt audio…). GlobalAudioPlayer marks these on
  // the second consecutive <audio> error and auto-skips to next.
  // Rows read this to render a muted "unavailable" state. Cleared on
  // new queue set so a fresh source of the same id gets a real retry.
  unavailableIds: string[];
  // Timestamp (ms since epoch) when each id in unavailableIds was
  // marked. Row-level `isUnavailable` UI expires marks after 5 minutes
  // so a transient network blip doesn't blackball the row for the
  // whole session — a tower handoff on mobile shouldn't need an app
  // refresh to recover. The queue-navigation skip (next/prev/onEnded)
  // still reads unavailableIds directly so auto-play doesn't loop on
  // a dead URL during the same listening pass.
  unavailableAt: Record<string, number>;
}

const initialState: PlayerState = {
  queue: [],
  index: -1,
  isPlaying: false,
  progress: 0,
  duration: 0,
  volume: 1,
  repeatMode: 'off',
  shuffle: false,
  expanded: false,
  unavailableIds: [],
  unavailableAt: {},
};

const slice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    // Load a queue and start at `startIndex`. Autoplay begins on the next
    // <audio> `canplay` event so buffering doesn't race the user.
    setQueue(state, action: PayloadAction<{ tracks: PlayerTrack[]; startIndex: number }>) {
      state.queue = action.payload.tracks;
      state.index = Math.max(0, Math.min(action.payload.startIndex, action.payload.tracks.length - 1));
      state.isPlaying = true;
      state.progress = 0;
      state.duration = 0;
      // Fresh queue = fresh chance for every track (URLs may have been
      // re-signed since the last attempt).
      state.unavailableIds = [];
      state.unavailableAt = {};
    },
    play(state) { state.isPlaying = true; },
    pause(state) { state.isPlaying = false; },
    toggle(state) { state.isPlaying = !state.isPlaying; },
    next(state) {
      // Skip forward over any tracks already marked unavailable this
      // session so cascading dead links don't stall the queue.
      const unavail = new Set(state.unavailableIds);
      let idx = state.index + 1;
      while (idx < state.queue.length && unavail.has(state.queue[idx].id)) idx++;
      if (idx < state.queue.length) {
        state.index = idx;
        state.isPlaying = true;
        state.progress = 0;
        state.duration = 0;
      }
    },
    prev(state) {
      // Skip backward over unavailables similarly.
      const unavail = new Set(state.unavailableIds);
      let idx = state.index - 1;
      while (idx >= 0 && unavail.has(state.queue[idx].id)) idx--;
      if (idx >= 0) {
        state.index = idx;
        state.isPlaying = true;
        state.progress = 0;
        state.duration = 0;
      } else {
        state.progress = 0;
      }
    },
    setProgress(state, action: PayloadAction<number>) { state.progress = action.payload; },
    setDuration(state, action: PayloadAction<number>) { state.duration = action.payload; },
    seek(state, action: PayloadAction<number>) { state.progress = action.payload; },
    setVolume(state, action: PayloadAction<number>) { state.volume = Math.max(0, Math.min(1, action.payload)); },
    clear() { return initialState; },
    // Called when the audio element reports it finished naturally.
    // Repeat/shuffle applied here so the audio element only ever knows
    // "load src, play, wait for ended" — the queue-navigation policy
    // lives in the slice.
    onEnded(state) {
      if (state.repeatMode === 'one') {
        // Same track again; the GlobalAudioPlayer effect resets currentTime.
        state.isPlaying = true;
        state.progress = 0;
        return;
      }
      if (state.shuffle && state.queue.length > 1) {
        // Pick a different index at random. Small pool = accept dupes.
        let next = Math.floor(Math.random() * state.queue.length);
        if (state.queue.length > 2 && next === state.index) {
          next = (next + 1) % state.queue.length;
        }
        state.index = next;
        state.isPlaying = true;
        state.progress = 0;
        state.duration = 0;
        return;
      }
      const unavail = new Set(state.unavailableIds);
      let idx = state.index + 1;
      while (idx < state.queue.length && unavail.has(state.queue[idx].id)) idx++;
      if (idx < state.queue.length) {
        state.index = idx;
        state.isPlaying = true;
        state.progress = 0;
        state.duration = 0;
      } else if (state.repeatMode === 'all' && state.queue.length > 0) {
        // Wrap to the first playable from the top.
        let wrap = 0;
        while (wrap < state.queue.length && unavail.has(state.queue[wrap].id)) wrap++;
        if (wrap >= state.queue.length) {
          // Entire queue is unavailable — stop.
          state.isPlaying = false;
          state.progress = 0;
          return;
        }
        state.index = wrap;
        state.isPlaying = true;
        state.progress = 0;
        state.duration = 0;
      } else {
        state.isPlaying = false;
        state.progress = 0;
      }
    },
    setRepeatMode(state, action: PayloadAction<'off' | 'one' | 'all'>) { state.repeatMode = action.payload; },
    cycleRepeat(state) {
      state.repeatMode = state.repeatMode === 'off' ? 'all' : state.repeatMode === 'all' ? 'one' : 'off';
    },
    setShuffle(state, action: PayloadAction<boolean>) { state.shuffle = action.payload; },
    toggleShuffle(state) { state.shuffle = !state.shuffle; },
    expand(state) { state.expanded = true; },
    collapse(state) { state.expanded = false; },
    toggleExpanded(state) { state.expanded = !state.expanded; },
    // Update-in-place — e.g. a My Songs poll returned a fresh signed URL
    // for a track that's currently in the queue (URL refresh flow).
    updateTrack(state, action: PayloadAction<{ id: string; patch: Partial<PlayerTrack> }>) {
      const idx = state.queue.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) state.queue[idx] = { ...state.queue[idx], ...action.payload.patch };
    },
    // Session marker for a track that failed to load. Auto-skip logic
    // in GlobalAudioPlayer emits this on the second consecutive error;
    // rows on My Songs read it to render a greyed-out "Unavailable" state.
    markUnavailable(state, action: PayloadAction<string>) {
      if (!state.unavailableIds.includes(action.payload)) {
        state.unavailableIds.push(action.payload);
      }
      // Timestamp the mark for the UI-side auto-expiry. Reading
      // Date.now() in a reducer is deterministic-enough here — it's a
      // session-scoped counter, not persisted, not replayed.
      state.unavailableAt[action.payload] = Date.now();
    },
    clearUnavailable(state, action: PayloadAction<string>) {
      state.unavailableIds = state.unavailableIds.filter((id) => id !== action.payload);
      delete state.unavailableAt[action.payload];
    },
  },
});

export const {
  setQueue, play, pause, toggle, next, prev, setProgress, setDuration, seek,
  setVolume, clear, onEnded, updateTrack,
  setRepeatMode, cycleRepeat, setShuffle, toggleShuffle,
  expand, collapse, toggleExpanded,
  markUnavailable, clearUnavailable,
} = slice.actions;
export default slice.reducer;
